import { PropertyHeader } from "@/components/PropertyHeader";
import { RegressionModelDetails } from "@/components/RegressionModelDetails";
import ResponsiveContainer from "@/components/ResponsiveContainer";
import { CompAdjustmentResult, ScatterplotSummary } from "@/components/ScatterPlotSummary";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import useFetch from "@/hooks/useFetch";
import { deriveChartStuff } from "@/lib/deriveChartStuff";
import { formatDate, formatPrice } from "@/lib/formatters";
import { normalizeDataBasedOnVersion } from "@/lib/PreparedDataset";
import { PageProps } from "@/types/PageProps";
import RegressionModel from "@/types/RegressionModel";
import { Adjustment } from "@/types/serializers";
import { Head } from "@inertiajs/react";
import { parseCalendarDate } from "@/lib/calendarDate";
import Logo from "../assets/fulllogo_transparent.png";

type Comp = { id: string };
type Props = PageProps & {
  adjustment: Adjustment;
  dataset_url: string | null;
  regression_model: RegressionModel | null;
  comps: Comp[];
  outliers: string[];
};

function CompAdjustments({
  comps,
  compAdjustmentResults,
}: {
  comps: Comp[];
  compAdjustmentResults: CompAdjustmentResult[];
}) {
  return (
    <div>
      <div className="font-bold pb-2">Comp Adjustments</div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Address</TableHead>
            <TableHead>Contract Date</TableHead>
            <TableHead className="text-right">Close Price</TableHead>
            <TableHead className="text-right">Calculated Effective Date Price</TableHead>
            <TableHead className="text-right">Adjustment</TableHead>
            <TableHead className="text-right">Percent Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {comps.map((comp) => {
            const result = compAdjustmentResults.find((r) => r.id === comp.id);
            if (!result) return null;

            return (
              <TableRow key={comp.id}>
                <TableCell>{result.address}</TableCell>
                <TableCell>{formatDate(result.saleDate.getTime())}</TableCell>
                <TableCell className="text-right">{formatPrice(result.salePrice)}</TableCell>
                <TableCell className="text-right">{formatPrice(result.adjustedValue)}</TableCell>
                {result.adjustmentAmount < 0 ? (
                  <TableCell className="text-right">
                    <span className="text-red-600 font-bold">
                      {formatPrice(result.adjustmentAmount)}
                    </span>
                  </TableCell>
                ) : (
                  <TableCell className="text-right">
                    <span className="text-green-600 font-bold">
                      +{formatPrice(result.adjustmentAmount)}
                    </span>
                  </TableCell>
                )}
                {result.percentChange < 0 ? (
                  <TableCell className="text-right">{result.percentChange.toFixed(1)}%</TableCell>
                ) : (
                  <TableCell className="text-right">{result.percentChange.toFixed(1)}%</TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function AppraiserNotes({ adjustment }: { adjustment: Adjustment }) {
  return (
    <div>
      <div className="font-bold pb-2">Appraiser Notes</div>
      <div className="text-sm whitespace-pre-wrap">{adjustment.appraiser_notes}</div>
    </div>
  );
}

function TrendMethodology({ regression_model }: { regression_model: RegressionModel }) {
  return (
    <div>
      <div className="font-bold pb-2">Trend Methodology</div>
      <RegressionModelDetails model={regression_model} className="text-sm" />
    </div>
  );
}

export default function Component({
  adjustment,
  dataset_url,
  comps,
  outliers,
  regression_model,
}: Props) {
  const { data, isPending, error } = useFetch(dataset_url);
  const scatterplotData = data
    ? normalizeDataBasedOnVersion(data, outliers, comps).filter((d) => !d.outlier)
    : [];

  const chartStuff =
    adjustment.effective_date && adjustment.address && scatterplotData.length > 0
      ? deriveChartStuff(scatterplotData, regression_model, {
          date: parseCalendarDate(adjustment.effective_date),
          name: adjustment.address || "",
        })
      : null;

  const compAdjustmentResults = chartStuff?.compAdjustmentResults;

  return (
    <>
      <Head title={`Summary - ${adjustment.address}`} />
      <Head>
        <style type="text/css">{`
          html {
            font-size: 14px;
          }
          .shadow, .shadow-xl, .shadow-2xl, .shadow-lg, .shadow-md, .shadow-sm {
            box-shadow: none !important;
          }
        `}</style>
      </Head>
      <div className="py-4">
        <div className="w-[920px] mx-auto">
          <img src={Logo} className="w-48 pb-4 mx-auto" />
          <h1 className="text-xl font-bold pb-4 pt-4">Market Condition Adjustments Summary</h1>
          <PropertyHeader adjustment={adjustment} />

          <div className="pt-8">
            <div className="w-full space-y-4">
              <div className="space-y-8">
                <CompAdjustments
                  comps={comps}
                  compAdjustmentResults={compAdjustmentResults || []}
                />

                <div className="sm:flex sm:space-x-8 space-y-4 sm:space-y-0">
                  <div className="flex-1">
                    <AppraiserNotes adjustment={adjustment} />
                  </div>
                  <div className="flex-1">
                    {regression_model && <TrendMethodology regression_model={regression_model} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-6">
            <div className="font-bold pb-2">Comparable Home Sales Trend</div>
            <div className="w-full">
              <Card>
                <CardContent className="pt-6">
                  {isPending && <div>Loading...</div>}
                  {error && <div>Error: {error}</div>}
                  {!isPending && !error && scatterplotData && (
                    <ResponsiveContainer aspectRatio={16 / 6} maxHeight={600}>
                      <ScatterplotSummary
                        width={700}
                        height={500}
                        data={scatterplotData}
                        regressionType={regression_model}
                        onPointClick={() => {}}
                        subject={
                          adjustment.effective_date && adjustment.address
                            ? {
                                date: parseCalendarDate(adjustment.effective_date),
                                name: adjustment.address || "",
                              }
                            : undefined
                        }
                        chartStuff={chartStuff}
                      />
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
